/**
 * Annotation Types
 * Supports flexible tagging at block, block-span, and inline-range granularity
 */

// --- Anchor Types ---

/** Tags a single block */
export interface BlockAnchor {
  type: 'block'
  blockId: string
}

/** Tags multiple consecutive blocks */
export interface BlockBoundarySnapshot {
  blockType: string
  textPrefix: string
  textSuffix: string
  blockIndex: number
  previousBlockId?: string
  nextBlockId?: string
}

export interface BlockSpanAnchor {
  type: 'blockSpan'
  startBlockId: string
  endBlockId: string // inclusive
  startSnapshot?: BlockBoundarySnapshot
  endSnapshot?: BlockBoundarySnapshot
}

/** Tags part of text within/across blocks. Includes context for resilient re-matching. */
export interface InlineRangeAnchor {
  type: 'range'
  startBlockId: string
  startOffset: number
  endBlockId: string
  endOffset: number
  // Context for resilience - enables fuzzy re-matching
  textBefore: string // ~20 chars before the tagged text
  taggedText: string // the actual tagged text
  textAfter: string // ~20 chars after the tagged text
}

export type AnnotationAnchor = BlockAnchor | BlockSpanAnchor | InlineRangeAnchor

// --- Annotation ---

/** An annotation attaches tags to content via an anchor */
export interface Annotation {
  id: string
  tags: string[]
  anchor: AnnotationAnchor
  createdAt: Date
  /**
   * ISO timestamp of when the anchor went dead (the anchored text was cut or
   * deleted). The annotation is kept for a grace window so a paste — into this
   * file or another — re-attaches it. Cleared as soon as the anchor resolves.
   */
  orphanedAt?: string
}

// --- Validation Status ---

export type AnnotationStatus = 'valid' | 'repaired' | 'orphaned'

export interface AnnotationWithStatus extends Annotation {
  status: AnnotationStatus
  /** Original anchor before repair (if repaired) */
  originalAnchor?: AnnotationAnchor
}

// --- Type Guards ---

export function isBlockAnchor(anchor: AnnotationAnchor): anchor is BlockAnchor {
  return anchor.type === 'block'
}

export function isBlockSpanAnchor(anchor: AnnotationAnchor): anchor is BlockSpanAnchor {
  return anchor.type === 'blockSpan'
}

export function isInlineRangeAnchor(anchor: AnnotationAnchor): anchor is InlineRangeAnchor {
  return anchor.type === 'range'
}

// --- Selection Context ---

/** Current selection state for annotation creation */
export interface SelectionContext {
  type: 'collapsed' | 'singleBlock' | 'multiBlock' | 'textRange'
  /** Block ID where selection starts */
  startBlockId: string
  /** Block ID where selection ends (same as start for single block) */
  endBlockId: string
  /** Character offset within start block (for text ranges) */
  startOffset?: number
  /** Character offset within end block (for text ranges) */
  endOffset?: number
  /** Selected text content (for text ranges) */
  selectedText?: string
}

/** Determines the appropriate anchor type based on selection */
export function getAnchorTypeFromSelection(selection: SelectionContext): 'block' | 'blockSpan' | 'range' {
  switch (selection.type) {
    case 'collapsed':
    case 'singleBlock':
      return 'block'
    case 'multiBlock':
      return 'blockSpan'
    case 'textRange':
      return 'range'
  }
}
